import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaHeart,
  FaInfoCircle,
  FaRegHeart,
  FaVolumeUp,
} from "react-icons/fa";

import { toAbsoluteTechniquePhotoUrl } from "../../../../../services/techniquesPhotoServices";

import {
  beltClasses,
  FALLBACK_IMG,
  hasJapaneseChars,
  speakJapanese,
} from "../utils/techniquesUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_IMG;
  }
}

export default function TechniqueCard({
  technique,
  isFavorite,
  canFavorite,
  onToggleFavorite,
  onOpen,
}) {
  const { image1_url, nom, famille, traduction, ceinture } = technique || {};

  const imgSrc = toAbsoluteTechniquePhotoUrl(image1_url) || FALLBACK_IMG;

  const [toKanaFn, setToKanaFn] = useState(null);
  const triedImport = useRef(false);

  useEffect(() => {
    if (triedImport.current) return;

    triedImport.current = true;

    (async () => {
      try {
        const mod = await import("wanakana");

        if (mod?.toKana) {
          setToKanaFn(() => mod.toKana);
        }
      } catch {
        // le module n'est pas indispensable
      }
    })();
  }, []);

  const jpForTts = useMemo(() => {
    const value = String(nom || "").trim();

    if (!value) return "";

    if (hasJapaneseChars(value)) return value;

    const cleaned = value.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

    return toKanaFn ? toKanaFn(cleaned) : cleaned;
  }, [nom, toKanaFn]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen?.();
    }
  };

  const stopAndRun = (event, callback) => {
    event.preventDefault();
    event.stopPropagation();
    callback?.();
  };

  return (
    <article className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={onOpen}
        onKeyDown={handleKeyDown}
        className="block w-full bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:bg-slate-950"
        style={{ aspectRatio: "551 / 617" }}
        aria-label={`Ouvrir ${nom || "la technique"}`}
      >
        <img
          src={imgSrc}
          alt={nom || "Technique"}
          className="h-full w-full object-contain transition duration-300 group-hover:scale-[1.02]"
          loading="lazy"
          onError={onImgError}
        />
      </button>

      <div className="space-y-3 p-3">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`inline-flex max-w-full truncate rounded-full px-2.5 py-1 text-[10px] font-bold ring-1 ${beltClasses(
              ceinture
            )}`}
            title={ceinture || ""}
          >
            {ceinture || "Ceinture ?"}
          </span>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={(event) =>
                stopAndRun(event, () => speakJapanese(jpForTts))
              }
              className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-200 hover:text-slate-900 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-700"
              title="Prononcer le nom japonais"
            >
              <FaVolumeUp />
            </button>

            <button
              type="button"
              onClick={(event) => stopAndRun(event, onOpen)}
              className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 transition hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60"
              title="Voir la fiche"
            >
              <FaInfoCircle />
            </button>

            <button
              type="button"
              onClick={(event) => stopAndRun(event, onToggleFavorite)}
              disabled={!canFavorite}
              className={`grid h-9 w-9 place-items-center rounded-xl ring-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
                isFavorite
                  ? "bg-red-50 text-red-600 ring-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-200 dark:ring-red-800/60"
                  : "bg-slate-100 text-slate-500 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-700"
              }`}
              title={
                canFavorite
                  ? isFavorite
                    ? "Retirer des favoris"
                    : "Ajouter aux favoris"
                  : "Connectez-vous pour gérer les favoris"
              }
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
            </button>
          </div>
        </div>

        <div>
          <h3 className="line-clamp-1 text-sm font-bold text-slate-900 dark:text-white">
            {nom || "Technique"}
          </h3>

          {traduction && (
            <p className="mt-0.5 line-clamp-1 text-xs italic text-slate-600 dark:text-slate-300">
              {traduction}
            </p>
          )}

          <p className="mt-1 line-clamp-1 text-[11px] text-slate-500 dark:text-slate-400">
            Famille : {famille || "—"}
          </p>
        </div>
      </div>
    </article>
  );
}